import Cookie from 'universal-cookie';
import { processALBOIDCAuth } from './OIDCUtils';

/**
 * Defines the current environment
 */
export interface envDef {
  // Which environment we are in.
  name: string;

  // lambda end point for URL param entry point.
  endPoint: string;

  // lambda endpoint for JWT Cognito entry point
  cognitoEndPoint: string;

  // content API endpoint for JWT-authenticated content fetching
  contentEndPoint: string;
}

export interface APIKeys {
  environmentId: string;
  previewKey: string;
  deliveryKey: string;
  managementKey: string;
}

export type envSuffix = {
  envSuffix: string;
};

const envMap = new Map<string, envSuffix>();

envMap.set('sandbox', {
  envSuffix: `-sandbox`,
});
envMap.set('localhost', {
  envSuffix: `-test`,
});
envMap.set('test', {
  envSuffix: `-test`,
});
envMap.set('production', {
  envSuffix: '',
});

/**
 * Returns the environment (localhost | sandbox | test | production) based on the hostname
 * Supports URL patterns: cms.theinstitutes.org (prod), cms-test.theinstitutes.org, cms-sandbox.theinstitutes.org, localhost
 * Also supports preview variants: preview.cms-{environment}.theinstitutes.org
 *
 * @returns environment string or null
 */
const getEnv = (): string | null => {
  const hostname = window.location.hostname;

  // Remove 'preview.' prefix if present
  const cleanHostname = hostname.replace(/^preview\./, '');

  // Match patterns: cms.theinstitutes.org, cms-{env}.theinstitutes.org, localhost
  const match =
    cleanHostname.match(/^cms(?:-(\w+))?\.theinstitutes\.org$/) ??
    cleanHostname.match(/^(localhost)$/);

  if (!match) {
    console.warn('Invalid hostname:', hostname);
    return null;
  }
  // Extract environment from hostname
  let env: string;
  if (match[1] === 'localhost') {
    env = 'localhost';
  } else if (match[1]) {
    // Has environment suffix (cms-{env}.theinstitutes.org)
    env = match[1];
  } else {
    // No environment suffix (cms.theinstitutes.org) = production
    env = 'production';
  }

  // Validate known environments
  if (['production', 'sandbox', 'test', 'localhost'].includes(env)) {
    return env;
  }

  console.warn('Unknown environment:', env);
  return null;
};

/**
 * Get the base API URL for the current environment
 */
const getBaseAPIUrl = (env: string): string => {
  return `https://api.cms${envMap.get(env)?.envSuffix}.theinstitutes.org`;
};

/**
 * Get environment configuration for the given environment name
 */
const getEnvConfig = (env: string): envDef => {
  const baseUrl = getBaseAPIUrl(env);
  return {
    name: env,
    endPoint: `${baseUrl}/kontent-ai/keys-preview`,
    cognitoEndPoint: `/internal/jwt`,
    contentEndPoint: `${baseUrl}/content`,
  };
};

/**
 * Get the auth token for legacy Kontent.ai API key authentication (token parameter only)
 * This is only used when token parameter is present in URL
 *
 * @returns a token string from URL Params only (for Kontent.ai API key fetching)
 */
const getLegacyToken = (): string | null => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);

  // Check for legacy token parameters in order of preference
  // 'token' takes precedence, then 'legacyToken', then 'legacy_token'
  if (urlParams.has('token')) {
    return urlParams.get('token');
  }

  if (urlParams.has('legacyToken')) {
    return urlParams.get('legacyToken');
  }

  if (urlParams.has('legacy_token')) {
    return urlParams.get('legacy_token');
  }

  // No legacy token parameters found
  return null;
};

/**
 * Get or generate JWT token for modern authentication
 * This handles the JWT-based authentication flow (no Kontent.ai API keys involved)
 *
 * As of June 2025, JWT tokens are automatically generated and set by nginx when OIDC headers are present.
 * The client should primarily rely on the JWT cookie that nginx sets automatically.
 * Manual OIDC processing is only used as a fallback for compatibility.
 *
 * @returns JWT token string or null
 */
const getJWTToken = async (): Promise<string | null> => {
  const cookie = new Cookie(document.cookie);
  const JWTCookie = cookie.get('jwt') ?? null;

  // If we already have a JWT cookie (likely set by nginx automatically), return it
  if (JWTCookie) {
    return JWTCookie;
  }

  // Fallback: Try to generate JWT via ALB OIDC authentication manually
  // This is mainly for backwards compatibility and testing
  const token = await processALBOIDCAuth();
  if (token) {
    // Store the generated token in a cookie for subsequent requests
    cookie.set('jwt', token, { path: '/' });
    return token;
  }

  return null;
};

/**
 * Fetch Kontent.ai API keys using legacy token parameter authentication
 * This is ONLY used when token parameter is present (legacy support)
 *
 * @returns API Keys or undefined if token parameter not present or request fails
 */
const fetchKeys = async (): Promise<APIKeys | undefined> => {
  try {
    const env = getEnv();
    const legacyToken = getLegacyToken();

    if (!env) {
      throw new Error('Unable to determine environment');
    }

    if (!legacyToken) {
      throw new Error(
        'Legacy token parameter not present - use JWT authentication instead',
      );
    }

    const envConfig = getEnvConfig(env);

    const response = await fetch(envConfig?.endPoint, {
      method: 'GET',
      headers: {
        'x-api-key': legacyToken,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status:${response.status}`);
    }

    const data: APIKeys = await response.json();
    return data;
  } catch (error) {
    console.log('Could not fetch legacy API keys:', error);
    return undefined;
  }
};

const fetchKeysWithToken = async (
  XAPIKey: string,
): Promise<APIKeys | undefined> => {
  try {
    const env = getEnv();

    if (!env) {
      throw new Error('Unable to determine environment');
    }

    const envConfig = getEnvConfig(env);
    const response = await fetch(envConfig.endPoint, {
      method: 'GET',
      headers: {
        'x-api-key': XAPIKey,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status:${response.status}`);
    }

    const data: APIKeys = await response.json();
    return data;
  } catch (error) {
    console.log('Could not fetch API keys with token:', error);
    return undefined;
  }
};

// Export all functions at the end of the file
export {
  fetchKeys,
  fetchKeysWithToken,
  getEnv,
  getEnvConfig,
  getLegacyToken,
  getJWTToken,
};
